import { Inject, Injectable } from "@angular/core"
import { DOCUMENT } from "@angular/platform-browser"
import { isEmpty } from "lodash"
import { Subject } from "rxjs/Subject"
import * as tinycolor from "tinycolor2"

import { DomEventsService } from "./dom-events.service"
import { MerchantReferralCampaignPages, MerchantReferralCampaignPagesColors } from "./shared/merchant-referral"

@Injectable()
export class DisplayService {
  private document: Document
  private modal_source = new Subject<boolean>()
  private page_background_source = new Subject<string>()
  private embed_source = new Subject<boolean>()

  is_embedded: boolean
  is_modal: boolean
  page_background_source$ = this.page_background_source.asObservable()
  modal_source$ = this.modal_source.asObservable()
  embed_source$ = this.embed_source.asObservable()

  constructor(
    private domEventsService: DomEventsService,
    @Inject(DOCUMENT) document: any
  ) {
    this.document = document
  }

  private addHexColorPrefix(color) {
    if (color.indexOf("#") === -1) {
      return `#${color}`
    }
    return color
  }

  addStyles(styles) {
    const style = this.document.createElement("style")
    this.document.head.appendChild(style)
    style.type = "text/css"
    style.appendChild(this.document.createTextNode(styles))
  }

  addStyleRule(rule) {
    const style = this.document.createElement("style")
    this.document.head.appendChild(style)
    const sheet: any = style.sheet

    if (sheet.insertRule) {
      return sheet.insertRule(rule, 0)
    } else if (sheet.addRule) {
      return sheet.addRule(rule, -1)
    } else {
      console.error(`addStyleRule failed for rule: ${rule}`)
    }
  }

  setEmbedded(value) {
    this.is_embedded = value
    this.embed_source.next(value)
    const htmlElementClassList = this.document.documentElement.classList

    if (this.is_embedded) {
      htmlElementClassList.add("embed")
    } else {
      htmlElementClassList.remove("embed")
    }
  }

  setModal(value) {
    this.is_modal = value
    this.modal_source.next(value)
  }

  private addStyleRules(style_element: HTMLStyleElement, style_rules: { selector: string; styles: string[] }[]) {
    const sheet: any = style_element.sheet
    if (sheet.insertRule) {
      style_rules.forEach((rule) => {
        sheet.insertRule(`${rule.selector} { ${rule.styles.join("; ")} }`, 0)
      })
    } else if (sheet.addRule) {
      // addRule is IE specific
      style_rules.forEach((rule) => {
        rule.styles.forEach((style) => {
          sheet.addRule(rule.selector, style, -1)
        })
      })
    }
  }

  setReferralMagicColors(colors: MerchantReferralCampaignPagesColors) {
    if (this.document.getElementById("referral-magic-page-colors")) {
      return
    }

    // Create a new style element, Firefox raise a security error if we try
    // to add the rule to an exising stylesheet
    const style_element = this.document.createElement("style")
    style_element.id = "referral-magic-page-colors"
    // @todo this should be flagged for testing
    this.document.head.appendChild(style_element)

    this.addStyleRules(style_element, [
      {
        selector: ".referral-magic-background",
        styles: [`background-color: ${colors.background}`]
      },
      {
        selector: ".main-container, .mat-dialog-container",
        styles: [`background-color: ${colors.container_background}`]
      }
    ])
  }

  setReferralMagicFont(font: string) {
    // If already present do not need to do anything
    if (this.document.getElementById("referral-magic-page-google-fonts")) {
      return
    }

    // Create a new style element and link element, Firefox raise a security error if we try
    // to add a style rule to an exising stylesheet
    const link_element = this.document.createElement("link")
    link_element.id = "referral-magic-page-google-fonts"
    link_element.rel = "stylesheet"
    link_element.href = `https://fonts.googleapis.com/css?family=${encodeURI(font).replace("%20", "+")}`
    this.document.head.appendChild(link_element)

    // Remove the font size information
    const font_name = font.split(":")[0]
    const style_element = this.document.createElement("style")
    this.document.head.appendChild(style_element)
    this.addStyleRules(style_element, [
      {
        selector:
          "body, h1, h2, h3, h4, h5, button, label, a, p, md-input, textarea, .referral-magic, .mat-input-element, .mat-form-field",
        styles: [`font-family: \"${font_name}\", sans-serif !important`]
      }
    ])
  }

  setReferralMagicPageStyles(pages: MerchantReferralCampaignPages) {
    if (pages.google_fonts) {
      this.setReferralMagicFont(pages.google_fonts)
    }

    if (!isEmpty(pages.colors)) {
      this.setReferralMagicColors(pages.colors)
    }

    if (pages.background_url && pages.background_url.medium_url) {
      this.setReferralMagicPageBackground(pages.background_url.medium_url)
    }

    if (!isEmpty(pages.css)) {
      this.addStyles(pages.css)
    }

    this.addStyleRule(`
      .referral-magic .mat-focused .mat-input-placeholder.mat-float,
      .referral-magic .mat-input-placeholder.mat-float:not(.mat-empty),
      .referral-magic-modal .mat-input-container
      .mat-input-placeholder.mat-float:not(.mat-empty),
      .referral-magic-modal .mat-input-container.mat-focused
      .mat-input-placeholder.mat-float,
      .referral-magic-modal .mat-input-container.mat-focused .mat-input-placeholder.mat-float:not(.mat-empty)
      .mat-form-field {
        color: ${pages.colors.text};
      }`)

    this.addStyleRule(`
      md2-pagination .md2-pagination li.active {
        background-color: ${pages.colors.link} !important;
        color: ${tinycolor(pages.colors.link).getBrightness() > 175 ? "#333" : "#fff"} !important;
      }`)
  }

  setReferralMagicPageBackground(background_url: string) {
    this.page_background_source.next(background_url)
  }

  getOffsetColor(color: string, amount = 12) {
    const wrapped_color = tinycolor(color)
    if (wrapped_color.isLight()) {
      return wrapped_color.darken(amount).toString()
    }
    return wrapped_color.lighten(amount).toString()
  }

  setApplicationLoaded() {
    this.domEventsService.triggerOnDocument("app_ready")
  }

  scrollToTop() {
    window.scrollTo(0, 0)
  }
}



// WEBPACK FOOTER //
// /home/runner/work/onelocal/onelocal/src/my-referral/src/app/display.service.ts